import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CompletedKycDetail,
  CompletedKycListItem,
  FileAnswer,
  KycAuditEvent,
  KycListFilters,
  KycListResult,
  KycMetrics,
  KycRecord,
  KycStatus,
  SubmissionByInvitationRequest,
  SubmissionResponse,
  UpdateKycReviewAnswersRequest,
  UpdateKycStatusRequest,
} from './models';
import { API_BASE_URL } from './api-base.token';

@Injectable({ providedIn: 'root' })
export class KycApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly kycPath = `${this.apiBaseUrl}/api/kyc`;
  private readonly submissionsPath = `${this.apiBaseUrl}/api/submissions`;

  getMetrics() {
    return this.http.get<KycMetrics>(`${this.kycPath}/metrics`);
  }

  list() {
    return this.http.get<KycRecord[]>(this.kycPath);
  }

  listWithFilters(filters?: KycListFilters) {
    const params: Record<string, string> = {};
    if (filters?.status) params['status'] = filters.status;
    if (filters?.search) params['search'] = filters.search;
    if (filters?.startDate) params['startDate'] = filters.startDate;
    if (filters?.endDate) params['endDate'] = filters.endDate;
    if (filters?.page != null) params['page'] = String(filters.page);
    if (filters?.limit != null) params['limit'] = String(filters.limit);
    return this.http.get<KycListResult>(this.kycPath, { params });
  }

  getDetail(id: string) {
    return this.http.get<KycRecord>(`${this.kycPath}/${id}`);
  }

  getAuditLog(id: string) {
    return this.http.get<KycAuditEvent[]>(`${this.kycPath}/${id}/audit`);
  }

  listCompleted() {
    return this.http.get<CompletedKycListItem[]>(`${this.kycPath}/completed`);
  }

  getCompletedDetail(id: string) {
    return this.http.get<CompletedKycDetail>(`${this.kycPath}/completed/${id}`);
  }

  updateStatus(id: string, payload: UpdateKycStatusRequest) {
    return this.http.patch<KycRecord>(`${this.kycPath}/${id}/status`, payload);
  }

  updateReviewAnswers(id: string, payload: UpdateKycReviewAnswersRequest) {
    return this.http.patch<SubmissionResponse>(`${this.kycPath}/${id}/review-answers`, payload);
  }

  submitByInvitation(payload: SubmissionByInvitationRequest) {
    return this.http.post<SubmissionResponse>(`${this.submissionsPath}/by-invitation`, payload);
  }

  uploadFile(file: File, prefix: string) {
    const body = new FormData();
    body.append('file', file);
    body.append('prefix', prefix);
    return this.http.post<Pick<FileAnswer, 'key' | 'filename'>>(
      `${this.submissionsPath}/proxy`,
      body,
    );
  }

  getLatestSubmissionAnswers(token: string) {
    return this.http.get<{ answers: Record<string, unknown> }>(
      `${this.submissionsPath}/by-invitation/latest`,
      { params: { token } },
    );
  }

  getSubmission(submissionId: string) {
    return this.http.get<SubmissionResponse>(`${this.submissionsPath}/${submissionId}`);
  }

  getSubmissionFileUrl(key: string) {
    return this.http.get<{ url: string }>(`${this.submissionsPath}/presign-download`, {
      params: { key },
    });
  }

  startReview(id: string) {
    return this.updateStatus(id, { status: 'in_review' as KycStatus });
  }
}
